import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';

import type EmberDataModel from '@ember-data/model';

import type { BoardPreviewMap, RecordData, Service } from 'webapp-types';
import { ModelName } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { fakeInstanceOf } from 'webapp/utils/model';

import ClipboardCopyComponent from 'webapp/components/clipboard-copy';
import CommonShareLinkSettingsComponent from 'webapp/components/common/share/link/settings';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormCheckboxSwitchComponent from 'webapp/components/form/checkbox-switch';
import FormInputComponent from 'webapp/components/form/input';
import ToolTipWithIconComponent from 'webapp/components/tool-tip-with-icon';

import type { Board } from 'webapp/models-custom';
import type BufferedModel from 'webapp/models-custom/buffer/buffered-model';

export interface Model extends BufferedModel<EmberDataModel> {
	isPublicEnabled: boolean;
	datawallUrl: string;
	canCurrentUserEdit: boolean;
	password: string;
	restrictIps: string[];
	allowAnonymousDateRangeEdit: boolean;
	allowAnonymousFilterEdit: boolean;
	getDatawallUrlWithPreviewHash: (previewHash?: BoardPreviewMap, isIframe?: boolean) => string;
	getInternalUrlWithPreviewHash: (previewHash?: BoardPreviewMap, isIframe?: boolean) => string;
	isAutoLoop?: boolean;
	loopDeplay?: number;
}

interface Args {
	model: Model; // Board | BoardLoop | Report
	previewHash?: BoardPreviewMap;
	trackingOrigin: string;
	onContentChange: () => void;
}

interface CommonShareViaUrlComponentInterface<T> {
	Args: T;
	Blocks: { default: [] }; // this is needed for yield
}

export default class CommonShareLinkComponent<T extends Args> extends Component<
	CommonShareViaUrlComponentInterface<T>
> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('infolayer') declare infolayerService: Service.Infolayer;
	@service('request') declare requestService: Service.Request;
	@service('store') declare storeService: Service.Store;
	@service('popup') declare popupService: Service.Popup;
	@service('account') declare accountService: Service.Account;
	@service('navigation') declare navigationService: Service.Navigation;

	get datawallUrl(): string {
		return this.args.model.getDatawallUrlWithPreviewHash(this.args.previewHash);
	}

	get internalUrl(): string {
		return this.args.model.getInternalUrlWithPreviewHash(this.args.previewHash);
	}

	get isBoard(): boolean {
		return fakeInstanceOf(this.args.model, ModelName.BOARD);
	}

	get isBoardLoop(): boolean {
		return fakeInstanceOf(this.args.model, ModelName.BOARD_LOOP);
	}

	get isReport(): boolean {
		return fakeInstanceOf(this.args.model, ModelName.REPORT);
	}

	get showBoardPermissions(): boolean {
		return this.isBoard && !this.currentUserService.isViewer;
	}

	get isPublicCheckboxDisabled() {
		return this.isBoard && this.currentUserService.isViewer;
	}

	get showAdvancedSettings(): boolean {
		const model = this.args.model;
		return model.canCurrentUserEdit && model.isPublicEnabled;
	}

	get publicLinkTooltipText() {
		const hideDataboxContent =
			this.accountService.hideDataboxNotWhiteLabeledContentForNonAdmins;
		let text = `Use this option to share outside the ${hideDataboxContent ? '' : 'Databox '}app.`;
		if (!hideDataboxContent) {
			text +=
				' <a href="https://help.databox.com/overview-shareable-link" target="_blank">Learn more</a>';
		}
		return text;
	}

	get modelName() {
		return this.isReport
			? ModelName.REPORT
			: this.isBoardLoop
				? ModelName.BOARD_LOOP
				: ModelName.BOARD;
	}

	@action
	changeIsPublicEnabled(value: boolean) {
		this.args.model.isPublicEnabled = value;
		this.save();
		this.args.onContentChange();
	}

	@action
	openBoardPermissions() {
		(this.args.model as Board).openBoardPermissions();
	}

	@action
	save(): void {
		if (this.args.model.isDirty) {
			if (!this.isReport) {
				this.analyticsService.event(TrackingEvent.DATABOARD_STREAMING_SETTINGS_AUTOSAVED);
			}

			this.args.model
				.save()
				.then(() => {
					if (this.isBoardLoop) {
						this.analyticsService.event(TrackingEvent.LOOP_EDITED, {
							origin: this.args.trackingOrigin,
						});
					}
					if (this.isReport) {
						this.infolayerService.ok('Report sharing settings saved!');
					} else {
						this.infolayerService.ok('Dashboard sharing settings saved!');
					}
				})
				.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		}
	}

	@action
	revokeToken(): void {
		const itemId = this.args.model.id,
			confirmModelName = this.isReport ? 'The report' : 'Dashboards',
			affirmativeFunc = () => {
				const layer = this.infolayerService.loading('Generating...');

				this.requestService
					.api<RecordData.Payload>('POST', `/${this.modelName}s/${itemId}/reset-token`)
					.then(
						(reply) => {
							layer.ok('Your shareable link has been refreshed!');
							this.storeService.pushPayload(reply);
						},
						(errorData) => {
							layer.close();
							throw errorData;
						},
					)
					.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
			};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: 'By generating a new URL, the current token will be revoked.',
			text: confirmModelName + ' accessed by the old URL will stop working.',
			confirmAction: 'Revoke',
			cancelAction: 'Cancel',
		});
	}

	@action
	copyInternalToClipboard(): void {
		this.analyticsService.event(
			TrackingEvent.SHARE_INTERNAL_URL_BY_COPY_PASTE,
			this.getShareUrlTrackingData(),
		);
		this.infolayerService.ok('Shareable internal link copied to clipboard!');
	}

	@action
	copyPublicToClipboard(): void {
		this.analyticsService.event(
			TrackingEvent.SHARE_PUBLIC_URL_BY_COPY_PASTE,
			this.getShareUrlTrackingData(),
		);
		this.infolayerService.ok('Shareable public link copied to clipboard!');
	}

	private getShareUrlTrackingData() {
		const type = this.isBoardLoop ? 'loop' : this.isReport ? 'report' : 'databoard';
		return {
			origin: this.args.trackingOrigin,
			type,
		};
	}
	<template>
		<div class="private-url-ct">
			<div>
				<a>
					<CommonUiIconComponent @name="Copy" @size="sm" />
					<span>Copy internal link</span>
					<ClipboardCopyComponent
						@text={{this.internalUrl}}
						@onAfterCopy={{this.copyInternalToClipboard}}
					/>
				</a>
				(works for users with access only)
			</div>
			{{#if this.showBoardPermissions}}
				<div class="permissions">
					<a {{on "click" this.openBoardPermissions}}>
						<CommonUiIconComponent @name="UserAccess" @size="sm" />
						Change permissions
					</a>
				</div>
			{{/if}}
		</div>

		<div class="public-url-ct">

			<div class="row row--toggle">
				<FormCheckboxSwitchComponent
					@name="isPublicEnabled"
					@selected={{@model.isPublicEnabled}}
					@isDisabled={{this.isPublicCheckboxDisabled}}
					@onChange={{this.changeIsPublicEnabled}}
				/>
				<label>
					<strong>Enable public link</strong>
					(anyone with the link can view)
					<ToolTipWithIconComponent
						@text={{this.publicLinkTooltipText}}
						@maxWidth={{230}}
						@theme={{this.navigationService.theme.id}}
					/>
				</label>
			</div>

			<p class={{unless @model.isPublicEnabled "disabled"}}>Copy this URL to share:</p>

			<div class="row row--url {{unless @model.isPublicEnabled 'disabled'}}">
				<FormInputComponent
					name="url"
					disabled={{true}}
					@value={{this.datawallUrl}}
					@size="small"
				/>
				<a class="button {{unless @model.isPublicEnabled 'disabled'}}">
					Copy
					<ClipboardCopyComponent
						@text={{this.datawallUrl}}
						@onAfterCopy={{this.copyPublicToClipboard}}
					/>
				</a>

				{{#if @model.canCurrentUserEdit}}
					<a class="grey revoke" {{on "click" this.revokeToken}}>
						<CommonUiIconComponent @name="Refresh" @size="sm" />
						<span>Regenerate URL</span>
					</a>
				{{/if}}
			</div>

		</div>

		{{#if this.showAdvancedSettings}}
			<div class="advanced-ct">

				<div class="row adv-title"><strong>Additional settings</strong></div>

				<CommonShareLinkSettingsComponent @model={{@model}} @onSave={{this.save}} />

				<div class="clear"></div>

			</div>
		{{/if}}
	</template>
}
